import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  MessageCircle, 
  QrCode, 
  Send, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock,
  Smartphone,
  LogOut,
  AlertCircle,
  Bug
} from 'lucide-react';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


import getApiUrl from '../../utils/getApiUrl';

const API_URL = getApiUrl();

const WhatsAppManager = () => {
  const [status, setStatus] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [debugMode, setDebugMode] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  
  // Fonction pour ajouter des logs de debug
  const addDebugLog = (type, message, data = null) => {
    const log = {
      time: new Date().toISOString(),
      type,
      message,
      data: data ? JSON.stringify(data, null, 2) : null
    };
    setDebugLogs(prev => [log, ...prev].slice(0, 50));
    console.log(`[WhatsApp Debug] ${type}: ${message}`, data);
  };
  
  // Formulaire d'envoi
  const [sendForm, setSendForm] = useState({
    to: '',
    message: ''
  });
  const [sendResult, setSendResult] = useState(null);

  // Récupérer le statut
  const fetchStatus = useCallback(async () => {
    const url = `${API_URL}/api/whatsapp/status`;
    addDebugLog('REQUEST', `Fetching status from: ${url}`);
    
    try {
      const response = await authFetch(url);
      addDebugLog('RESPONSE', `Status code: ${response.status}`, { ok: response.ok, statusText: response.statusText });
      
      const data = await response.json();
      addDebugLog('DATA', 'Status data received', data);
      
      setStatus(data);
      
      // Si en attente de QR, récupérer le QR code
      if (data.status === 'qr_pending' || data.status === 'qr_ready') {
        // Si qrCodeDataUrl déjà dans la réponse status, l'utiliser directement
        if (data.qrCodeDataUrl) {
          setQrData(data.qrCodeDataUrl);
        } else {
          fetchQrCode();
        }
      } else {
        setQrData(null);
      }
    } catch (err) {
      addDebugLog('ERROR', `Fetch error: ${err.message}`, { error: err.toString(), stack: err.stack });
      setStatus({ status: 'error', error: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  // Récupérer le QR code
  const fetchQrCode = async () => {
    const url = `${API_URL}/api/whatsapp/qr`;
    addDebugLog('REQUEST', `Fetching QR from: ${url}`);
    
    try {
      const response = await authFetch(url);
      addDebugLog('RESPONSE', `QR response code: ${response.status}`);
      
      const data = await response.json();
      addDebugLog('DATA', 'QR data received', { hasQrCode: !!data.qrCodeDataUrl, message: data.message });
      
      if (data.qrCodeDataUrl) {
        setQrData(data.qrCodeDataUrl);
      }
    } catch (err) {
      addDebugLog('ERROR', `QR fetch error: ${err.message}`);
      console.error('Erreur QR:', err);
    }
  };

  // Récupérer les messages
  const fetchMessages = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/whatsapp/messages?limit=20`);
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Erreur messages:', err);
    }
  };

  // Envoyer un message
  const handleSend = async (e) => {
    e.preventDefault();
    if (!sendForm.to || !sendForm.message) return;
    
    setSending(true);
    setSendResult(null);
    
    try {
      const res = await authFetch(`${API_URL}/api/whatsapp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: sendForm.to, message: sendForm.message })
      });
      
      if (res.ok) {
        setSendResult({ type: 'success', message: 'Message envoyé !' });
        setSendForm({ to: sendForm.to, message: '' });
        setTimeout(() => {
          fetchMessages();
          fetchStatus();
        }, 1000);
      } else {
        const errData = await res.json().catch(() => ({}));
        setSendResult({ type: 'error', message: errData.detail || errData.error || `Erreur ${res.status}` });
      }
    } catch (err) {
      setSendResult({ type: 'error', message: err.message });
    } finally {
      setSending(false);
    }
  };

  // Déconnexion
  const handleLogout = async () => {
    if (!window.confirm('Voulez-vous déconnecter WhatsApp ?')) return;
    
    try {
      await authFetch(`${API_URL}/api/whatsapp/logout`, { method: 'POST' });
      fetchStatus();
    } catch (err) {
      console.error('Erreur logout:', err);
    }
  };

  // Polling du statut
  useEffect(() => {
    fetchStatus();
    fetchMessages();
    
    // Polling toutes les 5 secondes si QR pending/ready, sinon 30 secondes
    const interval = setInterval(() => {
      fetchStatus();
    }, (status?.status === 'qr_pending' || status?.status === 'qr_ready') ? 5000 : 30000);
    
    return () => clearInterval(interval);
  }, [fetchStatus, status?.status]);

  // Rendu du statut
  const renderStatus = () => {
    if (!status) return null;
    
    const statusConfig = {
      ready: { color: 'bg-green-500', icon: CheckCircle, label: 'Connecté' },
      qr_pending: { color: 'bg-yellow-500', icon: QrCode, label: 'Scan QR requis' },
      initializing: { color: 'bg-blue-500', icon: Clock, label: 'Initialisation...' },
      disconnected: { color: 'bg-red-500', icon: XCircle, label: 'Déconnecté' },
      error: { color: 'bg-red-500', icon: AlertCircle, label: 'Erreur' }
    };
    
    const config = statusConfig[status.status] || statusConfig.error;
    const Icon = config.icon;
    
    return (
      <div className="flex items-center gap-2">
        <div className={`w-3 h-3 rounded-full ${config.color} ${status.status === 'ready' ? 'animate-pulse' : ''}`} />
        <Icon className="w-4 h-4" />
        <span className="font-medium">{config.label}</span>
        {status.connectedNumber && (
          <Badge variant="outline" className="ml-2">
            {status.connectedName} ({status.connectedNumber})
          </Badge>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" />
          Chargement...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Debug Panel */}
      <Card className="border-yellow-500/50 bg-yellow-500/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-yellow-500">
              <Bug className="w-5 h-5" />
              Debug Panel
            </CardTitle>
            <Button 
              variant={debugMode ? "destructive" : "outline"}
              size="sm" 
              onClick={() => setDebugMode(!debugMode)}
            >
              {debugMode ? "Masquer Debug" : "Afficher Debug"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm space-y-1">
            <p><strong>API_URL:</strong> <code className="bg-slate-800 px-1 rounded">{API_URL || '(vide)'}</code></p>
            <p><strong>WhatsApp Status:</strong> <code className="bg-slate-800 px-1 rounded">{status?.status || 'null'}</code></p>
            <p><strong>Version:</strong> <code className="bg-slate-800 px-1 rounded">{status?.version || 'N/A'}</code></p>
            <p><strong>Error:</strong> <code className="bg-red-800 px-1 rounded">{status?.error || 'Aucune'}</code></p>
          </div>
          
          {debugMode && debugLogs.length > 0 && (
            <div className="mt-4">
              <h4 className="font-bold mb-2">Logs ({debugLogs.length})</h4>
              <div className="max-h-60 overflow-y-auto bg-slate-900 rounded p-2 text-xs font-mono">
                {debugLogs.map((log, i) => (
                  <div key={i} className={`mb-2 ${log.type === 'ERROR' ? 'text-red-400' : log.type === 'REQUEST' ? 'text-blue-400' : 'text-green-400'}`}>
                    <span className="text-gray-500">{log.time.split('T')[1].split('.')[0]}</span>
                    {' '}[{log.type}] {log.message}
                    {log.data && <pre className="text-gray-400 ml-4">{log.data}</pre>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header Status */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              WhatsApp Service
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchStatus}
                data-testid="whatsapp-refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              {status?.status === 'ready' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleLogout}
                  className="text-red-600 hover:text-red-700"
                  data-testid="whatsapp-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            {renderStatus()}
            {status?.status === 'ready' && (
              <div className="text-sm text-gray-500">
                {status.messagesSent || 0} envoyés · {status.messagesReceived || 0} reçus
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* QR Code Section */}
      {(status?.status === 'qr_pending' || status?.status === 'qr_ready') && qrData && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <Smartphone className="w-5 h-5" />
              Connexion WhatsApp requise
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <p className="text-center text-yellow-700">
              Scannez ce QR code avec WhatsApp sur votre téléphone
              <br />
              <span className="text-sm">(WhatsApp &gt; Paramètres &gt; Appareils connectés)</span>
            </p>
            <div className="bg-white p-4 rounded-lg shadow-inner">
              <img 
                src={qrData} 
                alt="QR Code WhatsApp" 
                className="w-64 h-64"
                data-testid="whatsapp-qr-code"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={fetchQrCode}
              data-testid="whatsapp-refresh-qr-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser le QR code
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Formulaire d'envoi */}
      {status?.status === 'ready' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Envoyer un message
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Numéro de téléphone
                </label>
                <Input
                  type="tel"
                  placeholder="+33612345678"
                  value={sendForm.to}
                  onChange={(e) => setSendForm(prev => ({ ...prev, to: e.target.value }))}
                  data-testid="whatsapp-to-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Message
                </label>
                <Textarea
                  placeholder="Votre message..."
                  value={sendForm.message}
                  onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                  rows={3}
                  data-testid="whatsapp-message-input"
                />
              </div>
              
              {sendResult && (
                <div className={`p-3 rounded-lg ${
                  sendResult.type === 'success' 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {sendResult.type === 'success' ? (
                    <CheckCircle className="w-4 h-4 inline mr-2" />
                  ) : (
                    <XCircle className="w-4 h-4 inline mr-2" />
                  )}
                  {sendResult.message}
                </div>
              )}
              
              <Button 
                type="submit" 
                disabled={sending || !sendForm.to || !sendForm.message}
                className="w-full"
                data-testid="whatsapp-send-btn"
              >
                {sending ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Historique des messages */}
      {status?.status === 'ready' && messages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Derniers messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {messages.map((msg, index) => (
                <div 
                  key={msg.id || index}
                  className={`p-3 rounded-lg ${
                    msg.direction === 'outgoing' 
                      ? 'bg-green-50 ml-8' 
                      : 'bg-gray-50 mr-8'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-600">
                      {msg.direction === 'outgoing' ? 'Vous' : msg.fromName || msg.from}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(msg.timestamp).toLocaleString('fr-FR')}
                    </span>
                  </div>
                  <p className="text-sm">{msg.body}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Message si service non connecté */}
      {status?.status === 'disconnected' && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-6 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h3 className="font-medium text-red-700 mb-2">Service WhatsApp déconnecté</h3>
            <p className="text-sm text-red-600 mb-4">
              Le service WhatsApp n'est pas démarré ou a été déconnecté.
            </p>
            <Button variant="outline" onClick={fetchStatus}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Vérifier le statut
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="bg-gray-50">
        <CardContent className="py-4">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-blue-500" />
            À propos de ce service
          </h4>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Solution non-officielle basée sur WhatsApp Web</li>
            <li>Idéale pour communication interne (~20 msg/jour)</li>
            <li>Session persistante après le premier scan QR</li>
            <li>Ne pas utiliser pour du marketing de masse</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppManager;
